import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Plus, FileImage, FolderTree, SlashSquare, Plug, PackagePlus,
  ChevronRight, Github, Terminal, Globe, Database, BookOpen, Cpu,
  CircuitBoard, PlugZap
} from 'lucide-react';
import { CONNECTORS } from '@/lib/connectors/ConnectorRegistry';

const CONNECTOR_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Github, Terminal, Globe, Database, BookOpen, Cpu, CircuitBoard, PlugZap, FolderTree,
};

interface Props {
  onPickFile?: () => void;
  onPickFolder?: () => void;
  onOpenSlash?: () => void;
}

export function PlusMenu({ onPickFile, onPickFolder, onOpenSlash }: Props) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="focus-ring flex h-7 w-7 items-center justify-center rounded-md text-fg-muted hover:bg-surface-2 hover:text-fg"
          aria-label="Add"
        >
          <Plus className="h-4 w-4" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[230px] rounded-lg border border-border bg-canvas p-1 shadow-menu animate-fade-in"
        >
          <DropdownMenu.Item
            className="menu-item"
            onSelect={() => onPickFile?.()}
          >
            <FileImage className="h-3.5 w-3.5 text-fg-muted" />
            <span>Add files or photos</span>
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className="menu-item"
            onSelect={() => onPickFolder?.()}
          >
            <FolderTree className="h-3.5 w-3.5 text-fg-muted" />
            <span>Add folder</span>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-1 h-px bg-border" />

          <DropdownMenu.Item
            className="menu-item"
            onSelect={() => onOpenSlash?.()}
          >
            <SlashSquare className="h-3.5 w-3.5 text-fg-muted" />
            <span>Slash commands</span>
            <span className="menu-shortcut">/</span>
          </DropdownMenu.Item>

          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className="menu-item">
              <Plug className="h-3.5 w-3.5 text-fg-muted" />
              <span className="flex-1">Connectors</span>
              <ChevronRight className="h-3 w-3 text-fg-subtle" />
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent
                sideOffset={6}
                className="z-50 w-[220px] rounded-lg border border-border bg-canvas p-1 shadow-menu animate-fade-in"
              >
                {CONNECTORS.map((c) => {
                  const Icon = CONNECTOR_ICONS[c.icon] ?? Plug;
                  return (
                    <DropdownMenu.Item
                      key={c.id}
                      className="menu-item"
                      disabled={!c.configured}
                    >
                      <Icon className="h-3.5 w-3.5 text-fg-muted" />
                      <span className="flex-1">{c.label}</span>
                      {!c.configured && (
                        <span className="text-2xs text-fg-subtle">Configure</span>
                      )}
                      {c.hasSubmenu && (
                        <ChevronRight className="h-3 w-3 text-fg-subtle" />
                      )}
                    </DropdownMenu.Item>
                  );
                })}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>

          <DropdownMenu.Item className="menu-item" disabled>
            <PackagePlus className="h-3.5 w-3.5 text-fg-muted" />
            <span>Add plugins…</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
